import {MenuItem} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {ReactNode, useEffect} from 'react';

import {isNil} from 'ramda';

import {RequiredTestIdProps, useQueryState} from 'shared/web';

import {queryParams} from '../../utils/queryParams';
import {Page, PageProps} from '../Page/Page';
import {ControlledSection} from './types/ControlledSection';

export type SectionItem = {
  content: ReactNode;
} & MenuItem;

export type SectionsProps = {
  sections: SectionItem[];
  defaultSection?: string;
  activityPanel?: ReactNode;
  isLocked?: boolean;
  isFullHeight?: boolean;
  header?: ReactNode;
} & Pick<PageProps, 'renderHeader' | 'isNavigationCollapsed'> &
  RequiredTestIdProps &
  ControlledSection;

export function Sections(props: SectionsProps) {
  const [innerActiveSectionId, setInnerActiveSectionId] = useQueryState(
    queryParams.COMPONENT_SECTIONS_SECTION
  );

  const activeSectionId =
    props.activeSectionId ?? innerActiveSectionId ?? props.defaultSection ?? props.sections[0].id;

  const getSectionById = props.sections.find(({id}) => id === activeSectionId);

  useEffect(
    function syncActiveSectionFromProps() {
      if (props.activeSectionId === activeSectionId) {
        return;
      }

      if (isNil(props.activeSectionId)) {
        const isSelectedSection = props.sections.some((section) => section.id === activeSectionId);

        if (isSelectedSection) {
          return;
        }

        setInnerActiveSectionId(props.defaultSection ?? props.sections[0].id);
        return;
      }

      setInnerActiveSectionId(props.activeSectionId);
    },
    [props.activeSectionId] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const setActiveSectionId = props.onSectionChange ?? setInnerActiveSectionId;

  const handleSectionChange = (sectionId: string) => {
    setActiveSectionId(sectionId);
  };

  const navigation = props.sections.map((section) => ({
    ...section,
    href: `?${queryParams.COMPONENT_SECTIONS_SECTION}=${section.id}`,
  }));

  return (
    <Page
      isFullHeight={props.isFullHeight}
      isLocked={props.isLocked}
      header={props.header}
      renderHeader={props.renderHeader}
      isNavigationCollapsed={props.isNavigationCollapsed}
      navigation={navigation}
      activeItemId={activeSectionId}
      onItemSelect={handleSectionChange}
      activityPanel={props.activityPanel}
      data-testid={props['data-testid']}
    >
      <Box flex={1} minWidth={0} height={props.isFullHeight ? '100%' : undefined}>
        <HStack width="100%" height={props.isFullHeight ? '100%' : undefined}>
          {getSectionById?.content}
        </HStack>
      </Box>
    </Page>
  );
}
